import React from 'react'
import { Image, ImageBackground, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Button, Screen, Text } from '@components'
import { ScreenAuthProps } from '@navigation'
import { iconApp, welcomeImage, welcomeImageWeb } from '@assets'
import { AvailableLanguages, useLanguage } from '@context'
import { useLanguageStore } from '@store'
import getStyle from './style'
import { getCommonStyles } from '@utils'
import { useScreen } from '@hooks'

const LANGUAGES: { code: AvailableLanguages; labelKey: string }[] = [
  { code: 'fr', labelKey: 'welcome.languageFr' },
  { code: 'en', labelKey: 'welcome.languageEn' },
  { code: 'es', labelKey: 'welcome.languageEs' },
]

export const Welcome: React.FC<ScreenAuthProps<'Welcome'>> = ({ navigation }) => {
  const { t, currentLanguage } = useLanguage()
  const { top } = useSafeAreaInsets()

  const theme = useTheme()
  const { isMobile, isWeb } = useScreen()
  // Web grand écran : grand plan plein écran avec l'image haute résolution
  // (1600×2000) -> nette et large. Mobile/natif : image téléphone inchangée.
  const isWideWeb = isWeb && !isMobile
  const heroImage = isWideWeb ? welcomeImageWeb : welcomeImage
  const styles = getStyle(theme, top)
  const commonStyles = getCommonStyles(theme)

  return (
    <Screen noPaddingTop noPaddingBottom>
      <ImageBackground
        source={heroImage}
        style={styles.container}
        resizeMode="cover"
      >
        <View style={styles.topContainer}>
          <Image source={iconApp} style={styles.iconApp} />
          <Text type="titleBold" color={theme.colors.primary} style={styles.keeptouchTitle}>
            {t('welcome.title')}
          </Text>
          <Text type="bodySmallBold">{t('welcome.tagline')}</Text>
        </View>

        <View style={[styles.bottomContainer, !isMobile && commonStyles.flexMaxAuth]}>
          <Button
            title={t('welcome.connection')}
            onPress={() => navigation.navigate('Login')}
            borderWhite
          />
          <Button
            title={t('welcome.registration')}
            unfilled
            onPress={() => navigation.navigate('Register')}
            containerStyle={styles.registerButton}
          />
          <View style={isWideWeb ? styles.sloganFrame : undefined}>
            <Text type="body" color={theme.colors.white} style={styles.bottomText}>
              {t('welcome.slogan')}
            </Text>
            <View style={styles.languageRow}>
              {LANGUAGES.map(({ code, labelKey }) => (
                <TouchableOpacity
                  key={code}
                  testID={`welcome-language-${code}`}
                  accessibilityRole="button"
                  accessibilityState={{ selected: currentLanguage === code }}
                  onPress={() => useLanguageStore.getState().switchLanguage(code)}
                  style={styles.languageLink}
                >
                  <Text
                    type={currentLanguage === code ? 'bodySmallBold' : 'bodySmall'}
                    color={theme.colors.white}
                  >
                    {t(labelKey)}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
        <View />
      </ImageBackground>
    </Screen>
  )
}
